/**
 * E2E audio injection (dev/E2E builds only).
 *
 * Sibling of `e2eSpeechInjection.ts`, and deliberately a different thing.
 * They close different halves of the same gap:
 *
 *   - `e2eSpeechInjection` stubs at the **JS boundary**: the recording resolves
 *     from a canned `PronunciationResult` and the native recognizer never runs.
 *     Cheap, platform-neutral, and the right tool for asserting the recorder's
 *     JS state machine (PRON-001, CONV-005).
 *   - **this module** stubs at the **audio boundary**: the real native
 *     recognizer runs, connected to the real relay and the real Azure
 *     assessment, but its audio comes from a checked-in `.wav` instead of the
 *     microphone. It is the only way to exercise the recognizer, the phoneme
 *     extraction, and the score path — the segment `e2eSpeechInjection` skips
 *     over, and the segment that has never been verifiable without a human
 *     speaking into a device.
 *
 * The armed clip rides `AssessmentConfig.injectedAudioBase64` into the native
 * module, which swaps `SPXAudioConfiguration()` /
 * `AudioConfig.fromDefaultMicrophoneInput()` for a push stream fed from those
 * bytes. See `docs/specs/mobile/azure-speech-module.mobile.md` (SPEECH-MOD-005).
 *
 * Safety — three independent layers, because an audio-injection path that
 * survives into production is a microphone-shaped hole:
 *   1. The only producer is the `dev.speak-audio` handler in `devReset.ts`,
 *      which Metro dead-code-eliminates from release builds along with the rest
 *      of `src/dev`.
 *   2. `consumeInjectedAudio()` returns null unless `E2E_MODE` is on, so a
 *      value armed by some other means can never divert a real recording.
 *   3. The native push-stream branch is compiled out of release builds
 *      (`#if DEBUG` / `BuildConfig.DEBUG`). A release binary handed
 *      `injectedAudioBase64` **rejects** with `audio_injection_unsupported`
 *      rather than quietly falling back to the microphone — a silent fallback
 *      would turn a broken gate into a passing test.
 *
 * Consume-once, matching `e2eSpeechInjection`: one armed clip feeds exactly one
 * recording, so a leftover arm cannot bleed into the next branch.
 */

import { E2E_MODE } from '../config/constants';

/** An armed clip plus the provenance a run log needs to prove what was fed. */
export interface InjectedAudio {
  /** Base64 of a complete 16 kHz / 16-bit / mono `.wav`. */
  wavBase64: string;
  /** Fixture id it came from, for logging. */
  clipId: string;
  /** SHA-256 of the `.wav`, for logging. */
  sha256: string;
}

let armed: InjectedAudio | null = null;

/** Arm the next recognition to read from this clip (dev-intent only). */
export function armInjectedAudio(audio: InjectedAudio): void {
  armed = audio;
}

/**
 * Consume the armed clip for the recognition starting now, if any.
 * Returns null in production (`E2E_MODE` off) or when nothing is armed —
 * in both cases the recognizer uses the real microphone, unchanged.
 */
export function consumeInjectedAudio(): InjectedAudio | null {
  if (!E2E_MODE) return null;
  const value = armed;
  armed = null;
  return value;
}

/** Whether a clip is currently armed, without consuming it (diagnostics). */
export function hasInjectedAudio(): boolean {
  return E2E_MODE && armed !== null;
}

/** Drop any armed clip without consuming it (test teardown). */
export function clearInjectedAudio(): void {
  armed = null;
}
